import config from '../../config';
import { Api, ApiConfig } from '../api';
import { StreamApi } from '../streamApi';

export enum TOOL_NAME {
    GOOGLE_SEARCH = 'google_search',
    AMAZON_PRODUCT_SEARCH = 'amazon_product_search',
    GOOGLE_TRENDS_SEARCH = 'google_trends_search',
    FACEBOOK_ADS_SEARCH = 'facebook_ads_search',
    WEB_SCRAPER = 'web_scraper',
    ALIEXPRESS_IMAGE_PRODUCT_SOURCE = 'aliexpress_image_product_source',
    ALIEXPRESS_TEXT_PRODUCT_SEARCH = 'aliexpress_text_product_search',
    ADS_SEARCH = 'ads_search',
    DOC_TO_MARKDOWN = 'doc_to_markdown',
}

// 事件类型枚举
export enum EVENT_TYPES {
    // Agent 开始
    AGENT_START = 'agent.start',
    AGENT_COMPLETED = 'agent.completed',
    // 系统消息事件
    SYSTEM_MESSAGE_DELTA = 'system.message.delta',
    SYSTEM_MESSAGE_COMPLETED = 'system.message.completed',
    
    // 工具事件
    TOOL_MESSAGE = 'tool.message',
    TOOL_STARTED = 'tool.started',
    TOOL_COMPLETED = 'tool.completed',
    TOOL_SUMMARY_DELTA = 'tool.summary.delta',
    TOOL_SUMMARY_COMPLETED = 'tool.summary.completed',
    
    // 交付物事件
    ARTIFACT_CREATED = 'artifact.created',
    ARTIFACT_COMPLETED = 'artifact.completed',

    // user message
    USER_MESSAGE = 'user.message',

    // template modify completed
    TEMPLATE_MODIFY_COMPLETED = 'template.modify.completed',
    
    // round 事件
    ROUND_START = 'round.start',
    ROUND_COMPLETED = 'round.completed',

    // 整个Re-Act调用结束
    LOOP_COMPLETED = 'loop.completed',
    
    // 交互事件
    ACTION_BUTTON = 'action.button',
    // 错误事件
    ERROR = 'error',

    // verbose
    LLM_REQUEST = 'llm.request',

}

// 角色类型
export enum ROLES {
    USER = 'user',
    ASSISTANT = 'assistant',
    SYSTEM = 'system',
    TOOL = 'tool',
    ARTIFACT = 'artifact',
    AGENT = 'agent'
}

// 状态类型
export enum STATUSES {
    COMPLETED = 'completed',
    PENDING = 'pending',
    FAILED = 'failed',
    STREAMING = 'streaming',
    RUNNING = 'running',
    SUCCESS = 'success'
}

export enum AGENT_TYPE {
    CHAT_MODE = 'chat_mode',
    AGENT_MODE = 'agent_mode'
}

// 会话类型
export interface AgentConversation {
    id: string;
    user_id?: string;
    title: string;
    status?: 'active' | 'inactive' | 'archived';
    metadata?: Record<string, any>;
    message_count?: number;
    last_message_at?: string;
    created_at: string;
    updated_at: string;
}

// 基础消息字段
interface BaseMessageHistory {
    conversation_id?: string;
    message_id?: string;
    agent_type: AGENT_TYPE;
    created_at: string;
}

// 定义各种消息类型的具体结构
export interface AgentStart extends BaseMessageHistory {
    event_type: EVENT_TYPES.AGENT_START;
    role: ROLES.AGENT;
    agent_type: AGENT_TYPE;
    status: STATUSES.COMPLETED;
    content: string;
}

export interface AgentCompleted extends BaseMessageHistory {
    event_type: EVENT_TYPES.AGENT_COMPLETED;
    role: ROLES.AGENT;
    status: STATUSES.COMPLETED;
    content: string;
    agent_type: AGENT_TYPE;
    json_content: {
        type: 'agent_completed';
        summary: string;
    };
}

export interface SystemMessageDelta extends BaseMessageHistory {
    event_type: EVENT_TYPES.SYSTEM_MESSAGE_DELTA;
    role: ROLES.SYSTEM;
    status: STATUSES.STREAMING;
    delta: string;
    content: string;
}

export interface SystemMessageCompleted extends BaseMessageHistory {
    event_type: EVENT_TYPES.SYSTEM_MESSAGE_COMPLETED;
    role: ROLES.SYSTEM;
    status: STATUSES.COMPLETED;
    message_id: string;
    content: string;
    json_content: {
        type: 'system_message';
        round?: number;
    };
}

export interface ToolMessage extends BaseMessageHistory {
    event_type: EVENT_TYPES.TOOL_MESSAGE;
    role: ROLES.TOOL;
    status: STATUSES.COMPLETED;
    delta: string;
    content: string;
    round?: number;
}

export interface ToolStarted extends BaseMessageHistory {
    event_type: EVENT_TYPES.TOOL_STARTED;
    role: ROLES.TOOL;
    status: STATUSES.RUNNING;
    tool_name: string;
    tool_summary: string;
    tool_run_id: string;
    input_args: Record<string, any>;
    timestamp: number;
    round?: number;
}

export interface ToolCompleted extends BaseMessageHistory {
    event_type: EVENT_TYPES.TOOL_COMPLETED;
    role: ROLES.TOOL;
    status: STATUSES.SUCCESS | STATUSES.FAILED;
    tool_name: string;
    tool_run_id: string;
    output_struct: any;
    output_summary: string;
    latency_ms: number;
    round?: number;
}

export interface ToolSummaryDelta extends BaseMessageHistory {
    event_type: EVENT_TYPES.TOOL_SUMMARY_DELTA;
    role: ROLES.ASSISTANT;
    status: STATUSES.STREAMING;
    message_id: string;
    tool_run_id: string;
    index: number;
    delta: string;
    content: string;
    round?: number;
}

export interface ToolSummaryCompleted extends BaseMessageHistory {
    event_type: EVENT_TYPES.TOOL_SUMMARY_COMPLETED;
    role: ROLES.ASSISTANT;
    status: STATUSES.COMPLETED;
    content: string;
    json_content: {
        type: 'tool_summary_completed';
        summary: string;
    };
    round?: number;
}

export interface ArtifactCreated extends BaseMessageHistory {
    event_type: EVENT_TYPES.ARTIFACT_CREATED;
    role: ROLES.ARTIFACT;
    status: STATUSES.PENDING;
    artifact_id: string;
    artifact_type: string;
    title: string;
}

export interface ArtifactCompleted extends BaseMessageHistory {
    event_type: EVENT_TYPES.ARTIFACT_COMPLETED;
    role: ROLES.ARTIFACT;
    status: STATUSES.COMPLETED;
    content: string;
    artifact_id: string;
    title: string;
    file_url: string;
    format: string;
    preview: string;
}

export interface ErrorMessage extends BaseMessageHistory {
    event_type: EVENT_TYPES.ERROR;
    role: ROLES.ASSISTANT;
    status: STATUSES.FAILED;
    content: string;
    json_content: {
        type: 'error';
        error: string;
    };
}

export interface UserMessage extends BaseMessageHistory {
    event_type: EVENT_TYPES.USER_MESSAGE;
    role: ROLES.USER;
    status: STATUSES.COMPLETED;
    content: string;
    files?: FileItem[];
    json_content?: any;
}

export interface TemplateModifyCompleted extends BaseMessageHistory {
    event_type: EVENT_TYPES.TEMPLATE_MODIFY_COMPLETED;
    role: ROLES.ASSISTANT;
    status: STATUSES.COMPLETED;
    content: string;
    json_content: {
        type: 'template_modify_completed';
        new_template?: string;
        preview_data?: {
            template: string;
            productSet: any;
        };
        product_uuid?: string;
    };
}


export interface TaskStarted extends BaseMessageHistory {
    event_type: 'task.started';
    role: ROLES.TOOL;
    status: STATUSES.RUNNING;
    content: string;
    json_content: {
        type: 'task_started';
        task_uuid: string;
        task_type?: string;
        task_title?: string;
    };
}

export interface TaskCompleted extends BaseMessageHistory {
    event_type: 'task.completed';
    role: ROLES.TOOL;
    status: STATUSES.COMPLETED;
    content: string;
    json_content: {
        type: 'task_completed';
        task_uuid: string;
        task_type?: string;
        task_title?: string;
    };
}

export interface RoundStart extends BaseMessageHistory {
    event_type: EVENT_TYPES.ROUND_START;
    role: ROLES.ASSISTANT;
    status: STATUSES.COMPLETED;
    round?: number;
}

export interface RoundCompleted extends BaseMessageHistory {
    event_type: EVENT_TYPES.ROUND_COMPLETED;
    role: ROLES.ASSISTANT;
    status: STATUSES.COMPLETED;
    round?: number;
}

export interface LoopCompleted extends BaseMessageHistory {
    event_type: EVENT_TYPES.LOOP_COMPLETED;
    role: ROLES.ASSISTANT;
    status: STATUSES.COMPLETED;
    time_consume: number;
}

export interface ActionButton extends BaseMessageHistory {
    event_type: EVENT_TYPES.ACTION_BUTTON;
    role: ROLES.SYSTEM;
    status: STATUSES.COMPLETED;
    button_type: string;
}

export type MessageType = 
                SystemMessageDelta | 
                SystemMessageCompleted | 
                ToolStarted | 
                ToolCompleted | 
                ToolSummaryDelta | 
                ToolSummaryCompleted | 
                ArtifactCreated | 
                ArtifactCompleted | 
                AgentCompleted | 
                ErrorMessage | 
                UserMessage |
                TemplateModifyCompleted |
                ToolMessage |
                RoundStart |
                RoundCompleted |
                LoopCompleted |
                AgentStart |
                ActionButton


// 发送消息请求类型
export interface FileItem{
    file_type: string;
    url: string;
    name?: string;
    size?: number;
}

export interface SendMessageRequest {
    role: 'user' | 'assistant' | 'system';
    content: string;
    files?: FileItem[];
    dashboard_data?: any;
    file_id?: string;  // pulse 的 id
    metadata?: Record<string, any>;
    is_retry?: boolean;
}

// SSE事件类型
export interface SSEEvent {
    event: string;
    data: any;
}


// 工具结果类型
export interface ToolResult {
    id: string;
    message_id: string;
    tool_name: string;
    status: 'success' | 'failed' | 'pending';
    input_args: Record<string, any>;
    output_struct: Record<string, any>;
    output_summary: string;
    created_at: string;
}

// 任务产出物类型
export interface TaskArtifact {
    id: string;
    message_id: string;
    type: string;
    title: string;
    content: {
        format: string;
        body: string;
    };
    metadata: Record<string, any>;
    version: number;
    created_at: string;
    updated_at: string;
}

// 创建会话参数
export interface CreateConversationParams {
    title: string;
}

export interface UploadFileResponse {
    success: boolean;
    file_id: string;
    file_url: string;
    file_type: string;
    file_size: number;
    filename: string;
    message: string;
}

// 图片搜索产品请求参数
export interface ProductImageSearchParams {
    image_url: string;
    ship_to: string;
}

// 图片搜索产品响应类型
export interface SourceProductItem {
    product_id: string;
    title: string;
    url: string;
    image_url: string;
    price: {
        current: string;
        original: string;
        currency: string;
        discount_percentage: string;
    };
    category: {
        primary_id: string;
        primary_name: string;
        secondary_id: string;
        secondary_name: string;
    };
    rating: string;
    sales_volume: string;
    shipping: {
        from_country: string;
    };
    similarity_score?: number;
    platform: string;
}

// 完整的图片搜索响应结构
export interface ProductImageSearchApiResponse {
    success: boolean;
    source: string;
    request_id: string;
    total_count: number;
    products: SourceProductItem[];
}



export type ShopifyQueryResponse<T extends Record<string, any>> = T & {};

interface PaginationOptions {
    first?: number | null,
    last?: number | null,
    before?: string | null,
    after?: string | null
}

export interface ShopifyQueryParams {
    query: string;
    variables?: Record<string, any>;
}

// Brand Report interfaces
export interface BrandReportRequest {
    brand_name: string;
    brand_url: string;
    pulse_id: string;
    report_date: string;
}

export interface BrandReportResponse {
    success: boolean;
    message: string;
    data?: {
        brand_name: string;
        brand_url: string;
        report_id: string;
        created_at: string;
    };
}

// Pulse message interface
export interface PulseMessage {
    id: string;
    user_id: string;
    role: string;
    content: string;
    files: any[];
    modality: string;
    json_content: any;
    ordering: number;
    created_at: string;
}

export interface PulseHistoryResponse {
    messages: PulseMessage[];
    total_count: number;
    has_more: boolean;
}

export interface PulseHistoryParams {
    limit?: number;
    offset?: number;
    order_desc?: boolean;
}


export class AgentService {
    private api: Api;
    private streamApi: StreamApi;

    constructor(config: ApiConfig) {
        this.api = new Api(config);
        this.streamApi = new StreamApi({...config});
    }

    /**
     * 基础查询
     */
    public async queryShopify(params: ShopifyQueryParams): Promise<ShopifyQueryResponse<any>> {
        const { query, variables = {}} = params;
        return this.api.post<ShopifyQueryResponse<any>>(
            '/agent/shopify-graphql',
            {
                query,
                variables,
            }
        );
    }


    /**
     * 健康检查
     */
    public async health(): Promise<any> {
        return this.api.get<any>('/agent/health');
    }

    /**
     * 获取会话列表
     */
    public async getConversations(
    ): Promise<AgentConversation[]> {
        const queryParams = new URLSearchParams();
        queryParams.append('limit', '20');
        queryParams.append('offset', '0');
        
        const queryString = queryParams.toString();
        const url = queryString ? `/api/conversations?${queryString}` : '/api/conversations';
        
        return this.api.get<AgentConversation[]>(url);
    }

    /**
     * 创建新会话
     */
    public async createConversation(data: CreateConversationParams): Promise<AgentConversation> {
        return this.api.post<AgentConversation>('/api/conversations', {...data});
    }

    /**
     * 拉取消息历史（流式响应）
     */
    public async getMessages(
        conversationId: string,
    ): Promise<{json_content:MessageType, event_type: string}[]> {
        return this.api.get(
            `/api/conversations/${conversationId}/messages?limit=1000&offset=0`,
        );
    }


    /**
     * 发送消息（流式响应）
     */
    public async sendMessage(
        conversationId: string,
        data: SendMessageRequest,
        onMessage: (data: any) => void | Promise<void>,
        onError?: (error: any) => void,
        signal?: AbortSignal
    ): Promise<void> {
        return this.streamApi.post({
            url: `/api/conversations/${conversationId}/messages/stream`,
            body: {
                ...data, conversation_id: conversationId, modality: 'text', stream: true
            },
            onMessage: (data: any) => {
                onMessage(data);
            },
            onError,
            signal
        });
    }

    /**
     * Pulse streaming interface
     */
    public async pulseMessage(
        data: {
            content: string;
            stream?: boolean;
        },
        onMessage: (data: any) => void | Promise<void>,
        onError?: (error: any) => void,
        signal?: AbortSignal
    ): Promise<void> {
        return this.streamApi.post({
            url: '/api/pulse/stream',
            body: {...data,},
            onMessage: (data: any) => {
                onMessage(data);
            },
            onError,
            signal
        });
    }

    /**
     * Get pulse history messages
     */
    public async getPulseHistory(
        params: PulseHistoryParams = {}
    ): Promise<PulseHistoryResponse> {
        const queryParams = new URLSearchParams();
        
        if (params.limit !== undefined) {
            queryParams.append('limit', params.limit.toString());
        }
        if (params.offset !== undefined) {
            queryParams.append('offset', params.offset.toString());
        }
        if (params.order_desc !== undefined) {
            queryParams.append('order_desc', params.order_desc.toString());
        }
        
        const url = `/api/pulse/stream${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
        return this.api.get<PulseHistoryResponse>(url);
    }




    /**
     * 获取某条消息关联的工具结果
     */
    public async getToolResults(messageId: string): Promise<ToolResult[]> {
        return this.api.get<ToolResult[]>(`/api/messages/${messageId}/tool_results`);
    }

    /**
     * 获取某条消息关联的任务产出物结果
     */
    public async getTaskArtifacts(messageId: string): Promise<TaskArtifact[]> {
        return this.api.get<TaskArtifact[]>(`/api/messages/${messageId}/artifacts`);
    }

    /**
     * 上传图片进行搜索
     */
    public async uploadFile(file: File): Promise<UploadFileResponse> {
        const formData = new FormData();
        formData.append('file', file);

        // 上传接口走 account 域名，默认从 auth 配置读取并兼容旧环境变量回退
        const javaUploadUrl = `${config.services.auth.apiHost}/api/upload_file`;
        return this.api.post<UploadFileResponse>(javaUploadUrl, formData, {headers: {'Content-Type': 'multipart/form-data'}});
    }

    /**
     * 根据图片搜索相似产品
     * @param params 图片搜索参数
     * @returns 产品搜索结果
     */
    public async searchProductsByImage(params: ProductImageSearchParams): Promise<ProductImageSearchApiResponse> {
        return this.api.post<ProductImageSearchApiResponse>('/api/products/image-search', params);
    }

    /**
     * 查询订单
     */
    public async queryOrders(
        pagination: PaginationOptions = {},
        queryString: string = ''
    ): Promise<ShopifyQueryResponse<{
            orders: {
                edges: { cursor: string; node: any }[];
                pageInfo: {
                    hasPreviousPage: boolean;
                    hasNextPage: boolean;
                    startCursor: string;
                    endCursor: string;
                }
            }
        }>> {
        const gql = `
            query GetOrders(
            $first: Int
            $last: Int
            $after: String
            $before: String
            $query: String
            ) {
            orders(
                sortKey: CREATED_AT
                reverse: true
                first: $first
                last: $last
                after: $after
                before: $before
                query: $query
            ) {
                edges {
                cursor
                node {
                    id
                    name
                    createdAt
                    displayFulfillmentStatus
                    totalPriceSet {
                    shopMoney {
                        amount
                        currencyCode
                    }
                    }
                    totalShippingPriceSet {
                    shopMoney {
                        amount
                        currencyCode
                    }
                    }
                    shippingAddress {
                    address1
                    address2
                    city
                    province
                    country
                    zip
                    phone
                    }
                    email
                    phone
                    lineItems(first: 50) {
                    edges {
                        node {
                        id
                        title
                        sku
                        variantTitle
                        quantity
                        vendor
                        originalTotalSet {
                            shopMoney {
                            amount
                            currencyCode
                            }
                        }
                        product {
                            id
                            title
                            productType
                            media(first: 1) {
                            edges {
                                node {
                                ... on MediaImage {
                                    image {
                                    originalSrc
                                    }
                                }
                                }
                            }
                            }
                        }
                        variant {
                            id
                            title
                            price
                            sku
                            inventoryQuantity
                            inventoryItem {
                            unitCost {
                                amount
                                currencyCode
                            }
                            }
                            media(first: 1) {
                            edges {
                                node {
                                ... on MediaImage {
                                    image {
                                    originalSrc
                                    }
                                }
                                }
                            }
                            }
                        }
                        }
                    }
                    }
                }
                }
                pageInfo {
                hasPreviousPage
                hasNextPage
                startCursor
                endCursor
                }
            }
            }
        `;
    
        const variables = {
            first: pagination.first ?? null,
            last: pagination.last ?? null,
            after: pagination.after ?? null,
            before: pagination.before ?? null,
            query: queryString || null
        };
    
        return this.queryShopify({query: gql, variables});
    }

    /**
     * Create a brand report
     * @param request - Brand report request data
     * @returns Promise with brand report response
     */
    public async createBrandReport(request: BrandReportRequest): Promise<BrandReportResponse> {
        return this.api.post<BrandReportResponse>('/api/brand-report', {...request,});
    }
    
}

export const agentService = new AgentService({
    // baseURL: 'http://192.168.0.106:8000',
    baseURL: config.services.agent.apiHost,
    headers: {
        'Content-Type': 'application/json',
        // 'x-api-key': config.services.agent.apiKey,
        'x-api-key': '123'
    },
    debugMode: false
});
